import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../types/database';
import { AuthService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (phone: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const login = async (phone: string) => {
    const { user: loggedInUser, error } = await AuthService.login(phone);
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    setUser(loggedInUser);
    return { success: true };
  };

  const logout = async () => {
    await AuthService.logout();
    setUser(null);
  };

  const isAdmin = Boolean(user?.is_admin);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
